import React, { useContext, useEffect, useState, useRef } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Svg } from "@natera/material/lib/svg";
import backIcon from "@assets/svg/icons/billing-updates-back.svg";
import BillingUpdatesPageNotificationCard from "@app/components/billingUpdatesPageNotificationCard";
import { PreBillAPIContext } from "@app/preBill/provider";
import { UppAuthContext } from "@app/provider";
import { Utils } from "@natera/material/lib/date";
import { redirectTo } from "@app/utils";
import "./billingUpdates.scss";

const messages = defineMessages({
  billingUpdatesTitle: {
    id: "billingUpdatesTitle",
    defaultMessage: "Billing updates",
  },
});

interface Notification {
  id: string;
  testType: string;
  date: string | null;
  isRead: boolean;
  tokenizedLink: string;
}

const BillingUpdates: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { listActiveOrdersForPatient } = useContext(PreBillAPIContext);
  const { profile } = useContext(UppAuthContext);
  const patientUID = profile?.patientUID;
  const isInitialLoadDone = useRef(false);

  useEffect(() => {
    // Skip the request for users who don't have a patientUID yet (fresh signups,
    // pending patient matching). The server rejects such calls with
    // "Authenticated user with patientUID required"; the effect re-runs once
    // matching completes and profile.patientUID becomes defined.
    if (!patientUID) return;

    let mounted = true;

    const loadNotifications = async () => {
      if (isInitialLoadDone.current) {
        return;
      }

      try {
        const response = await listActiveOrdersForPatient();

        if (!mounted) return;

        const transformedNotifications = response.activeOrders.flatMap(
          (order, index) =>
            order.testTypeInformation.map((testInfo, testIndex) => ({
              id: `${index + 1}-${testIndex + 1}`,
              testType: testInfo.testType,
              date: Utils.getUSLocaleDate(new Date(order.tokenExpireDate)),
              isRead: order.lastOrderSeen !== null,
              tokenizedLink: order.tokenizedLink,
            }))
        );

        setNotifications(transformedNotifications);
        isInitialLoadDone.current = true;
      } catch (error) {
        console.error("Failed to load notifications:", error);
      }
    };

    loadNotifications();

    return () => {
      mounted = false;
    };
  }, [patientUID]);

  const handleNotificationClick = async (tokenizedLink: string) => {
    redirectTo(tokenizedLink);
  };

  return (
    <div className="billing-updates">
      <header className="billing-updates__header">
        <Svg
          data-testid="billing-updates-back-icon"
          className="billing-updates__back-icon"
          content={backIcon}
          onClick={() => navigate(-1)}
        />
        <h1 className="billing-updates__title">
          {intl.formatMessage(messages.billingUpdatesTitle)}
        </h1>
      </header>
      <div
        className="billing-updates__notifications"
        data-testid="billing-updates-notifications"
      >
        {notifications.map((notification) => (
          <BillingUpdatesPageNotificationCard
            key={notification.id}
            notification={notification}
            onClick={() => handleNotificationClick(notification.tokenizedLink)}
          />
        ))}
      </div>
    </div>
  );
};

export default BillingUpdates;
